<template>
    <Toast />
    <Sidebar v-model:visible="showMessageSidebar" position="right" style="width: 60%;" :modal="true" @hide="closeMessageSidebar" :showCloseIcon="false" class="message-sidebar">
        <template #header>
            <div class="custom-sidebar-header">
                <div v-if="!isCreatingMessage" class="sidebar-header-left">
                    <div class="sidebar-header-title">Messages</div>
                    <Button class="icon-btn" icon="pi pi-plus" @click="startNewMessage" />
                </div>
                <div class="sidebar-header-right">
                    <div class="sidebar-header-right-container" v-if="isCreatingMessage">
                        <div class="sidebar-header-title">Create Messages</div>
                        <Button class="close-btn" icon="pi pi-times" @click="closeMessageSidebar" />
                    </div>
                    <div class="sidebar-header-right-container-close" v-else>
                        <Button class="close-btn" icon="pi pi-times" @click="closeMessageSidebar" />
                    </div>
                </div>
            </div>
        </template>
        <Messaging 
            ref="messagingComponent"
            @close="closeMessageSidebar" 
            :message="selectedMessage"
            v-model:isCreating="isCreatingMessage"
            :encryptionId="opportunityId"
        />
    </Sidebar>
    <div class="message-btn-container">
        <Button label="Message" class="p-button-primary" @click="openMessageSidebar">
            <img src="@/assets/message.svg" alt="Message" class="message-icon" />
            Message
        </Button>
    </div>

    <div class="opportunity-container">
        <!-- Header Section -->
        <div class="header-section">
            <div class="header-title">
                <Skeleton v-if="loading" height="1.5rem" width="70%"></Skeleton>
                <span v-else class="rfp-title">{{ opportunityDetails?.name }}</span>
            </div>
            <div class="header-meta">
                <Skeleton v-if="loading" height="1.5rem" width="8rem"></Skeleton>
                <span v-else class="opportunity-id-badge">Opportunity ID {{ opportunityDetails?.rfp_id }}</span>
                <Skeleton v-if="loading" height="1.5rem" width="8rem"></Skeleton>
                <span v-else class="timer-badge" :class="{ 'ended': isRfpEnded }">
                    {{ isRfpEnded ? 'RFP has ended' : countdown ? `${countdown.days}d ${countdown.hours}h ${countdown.minutes}m ${countdown.seconds}s` : 'RFP has ended' }}
                </span>
            </div>
        </div>

        <!-- Only show these sections if not showing pricing table -->
        <template v-if="!showPricingTable">
            <Card class="intro-section">
                <template #title>
                    <div class="intro-title-section">
                        <div>
                            <h3 class="intro-title">Intro</h3>
                            <!-- <span class="intro-subtitle">Request for Proposal (RFP) for Outsourcing Services</span> -->
                        </div>
                        <div class="date-badge-container">
                            <Skeleton v-if="loading" height="1.5rem" width="8rem"></Skeleton>
                            <span v-else class="date-badge">Start date: {{ formatDate(opportunityDetails?.published_at) }}</span>
                            <Skeleton v-if="loading" height="1.5rem" width="8rem"></Skeleton>
                            <span v-else class="date-badge">End date: {{formatDate(opportunityDetails?.end_date)}}</span>
                        </div>
                    </div>
                </template>
                <template #content>
                    <div v-if="loading" class="intro-content">
                        <div class="skeleton-section">
                            <Skeleton height="2rem" width="70%" class="mb-2"></Skeleton>
                            <Skeleton height="1rem" width="90%" class="mb-2"></Skeleton>
                            <Skeleton height="1rem" width="85%" class="mb-2"></Skeleton>
                            <Skeleton height="1rem" width="80%" class="mb-2"></Skeleton>
                        </div>
                        <div class="skeleton-section">
                            <Skeleton height="2rem" width="60%" class="mb-2"></Skeleton>
                            <Skeleton height="1rem" width="95%" class="mb-2"></Skeleton>
                            <Skeleton height="1rem" width="90%" class="mb-2"></Skeleton>
                            <Skeleton height="1rem" width="85%" class="mb-2"></Skeleton>
                        </div>
                        <div class="skeleton-section">
                            <Skeleton height="2rem" width="65%" class="mb-2"></Skeleton>
                            <Skeleton height="1rem" width="88%" class="mb-2"></Skeleton>
                            <Skeleton height="1rem" width="92%" class="mb-2"></Skeleton>
                        </div>
                        <div class="skeleton-section">
                            <Skeleton height="2rem" width="55%" class="mb-2"></Skeleton>
                            <div class="skeleton-list">
                                <Skeleton height="1rem" width="80%" class="mb-2"></Skeleton>
                                <Skeleton height="1rem" width="75%" class="mb-2"></Skeleton>
                                <Skeleton height="1rem" width="85%" class="mb-2"></Skeleton>
                            </div>
                        </div>
                    </div>
                    <div v-else class="intro-content" v-html="opportunityDetails?.description"></div>
                </template>
            </Card>

            <!-- Links Section -->
            <Card class="links-section">
                <template #title>
                    Links
                </template>
                <template #content>
                    <div v-if="loading" class="links-list">
                        <Skeleton height="1.5rem" width="100%" class="mb-2"></Skeleton>
                        <Skeleton height="1.5rem" width="100%" class="mb-2"></Skeleton>
                    </div>
                    <ul v-else class="links-list">
                        <li v-for="link in opportunityDetails?.links" :key="link.linkUrl">
                            <a :href="link.linkUrl" target="_blank">{{ link.linkDescription }}</a>
                        </li>
                        <li v-if="!opportunityDetails?.links?.length" class="no-data">No links available</li>
                    </ul>
                </template>
            </Card>

            <!-- Attachments Section -->
            <Card class="attachments-section">
                <template #title>
                    Attachments
                </template>
                <template #content>
                    <div v-if="loading" class="attachments-list">
                        <Skeleton height="1.5rem" width="100%" class="mb-2"></Skeleton>
                        <Skeleton height="1.5rem" width="100%" class="mb-2"></Skeleton>
                    </div>
                    <div v-else class="attachments-list">
                        <div v-for="attachment in opportunityDetails?.attachments" :key="attachment.fileUrl" class="attachment-item">
                            <a href="javascript:void(0)" @click="downloadAttachment(attachment)" class="file-name">{{ attachment.fileName }}</a>
                        </div>
                        <div v-if="!opportunityDetails?.attachments?.length" class="no-data">No attachments available</div>
                    </div>
                </template>
            </Card>

            <!-- Action Buttons -->
            <Card>
                <template #content>
                    <div v-if="loading" class="action-buttons">
                        <Skeleton height="2rem" width="100px" class="mb-2"></Skeleton>
                        <Skeleton height="2rem" width="100px" class="mb-2"></Skeleton>
                    </div>
                    <div v-else class="action-buttons">
                        <template v-if="opportunityDetails?.vendor_status_id === 8">
                            <Button label="Decline" class="p-button-outlined p-button-secondary" @click="handleDecline" />
                            <Button label="Participate" class="p-button-primary" @click="onParticipate" />
                        </template>
                        <template v-else-if="opportunityDetails?.vendor_status_id === 17">
                            <div class="status-message declined">
                                <i class="pi pi-times-circle"></i>
                                <span>RFP has been declined</span>
                            </div>
                        </template>
                        <template v-else>
                            <Button label="Next" class="p-button-primary" @click="togglePricingTable" />
                        </template>
                    </div>
                </template>
            </Card>
        </template>


        <!-- Pricing Table Section -->
        <template v-else>
            <div class="pricing-tables-btns-intro">
                <div class="pricing-tables-toggle-container">
                    <Button v-for="(table, index) in opportunityDetails?.pricing_data" 
                        :key="table.table_name"
                        @click="active = index" 
                        :class="{ 'active': active === index }" 
                        :label="table.table_name"
                        class="pricing-tables-toggle-btn" />
                    <Button v-for="(group, groupName, index) in groupedQuestions"
                        :key="groupName"
                        @click="active = opportunityDetails?.pricing_data?.length + index"
                        :class="{ 'active': active === opportunityDetails?.pricing_data?.length + index }"
                        :label="groupName"
                        class="pricing-tables-toggle-btn" />
                </div>

                <div class="pricing-tables-intro">
                    <Button label="See Intro" class="p-button-primary" @click="toggleIntro" />
                </div>
            </div>

            <TabView class="pricing-tables-tabview" v-model:activeIndex="active">
                <TabPanel v-for="(table, tableIndex) in opportunityDetails?.pricing_data" :key="table.table_name">
                    <div class="pricing-table-section">
                        <div class="pricing-table-header">
                            <p class="pricing-table-section-title">{{ table.table_name }}</p>
                        </div>
                        
                        <!-- Nested tables navigation -->
                        <div v-if="table.table_data.allowNestedTables" class="nested-tables-nav">
                            <Button 
                                v-if="!table.nestedTables || table.nestedTables.length === 0"
                                @click="activeNestedTable[tableIndex] = undefined"
                                :class="{ 'active': activeNestedTable[tableIndex] === undefined }"
                                :label="table.table_name"
                                class="nested-table-btn" />
                            <div v-for="(nestedTable, nestedIndex) in table.nestedTables"
                                :key="nestedTable.table_name"
                                class="nested-table-item">
                                <div v-if="editingNestedTableName.tableIndex === tableIndex && editingNestedTableName.nestedIndex === nestedIndex" 
                                    class="nested-table-edit-container">
                                    <InputText 
                                        v-model="editingNestedTableName.newName" 
                                        class="nested-table-edit-input"
                                        @keyup.enter="confirmEditNestedTableName(tableIndex, nestedIndex)"
                                        @keyup.esc="cancelEditNestedTableName"
                                        ref="nestedTableNameInput"
                                        :disabled="isInputDisabled" />
                                </div>
                                <Button 
                                    v-else
                                    @click="activeNestedTable[tableIndex] = nestedIndex"
                                    :class="{ 'active': activeNestedTable[tableIndex] === nestedIndex }"
                                    :label="nestedTable.table_name"
                                    class="nested-table-btn"
                                    @dblclick="startEditNestedTableName(tableIndex, nestedIndex)" />
                                <Button 
                                    v-if="editingNestedTableName.tableIndex === tableIndex && editingNestedTableName.nestedIndex === nestedIndex"
                                    icon="pi pi-check" 
                                    class="p-button-success p-button-text edit-confirm-btn" 
                                    @click="confirmEditNestedTableName(tableIndex, nestedIndex)" />
                                <Button 
                                    v-if="editingNestedTableName.tableIndex === tableIndex && editingNestedTableName.nestedIndex === nestedIndex"
                                    icon="pi pi-times" 
                                    class="p-button-danger p-button-text edit-cancel-btn" 
                                    @click="cancelEditNestedTableName" />
                            </div>
                            <Button v-if="table.table_data.allowNestedTables" 
                                label="Add Table" 
                                class="p-button-primary add-table-btn" 
                                @click="addNestedTable(tableIndex)" />
                        </div>

                        <!-- Table content -->
                        <div v-if="!table.table_data.allowNestedTables || (table.table_data.allowNestedTables && (!table.nestedTables || table.nestedTables.length === 0) && activeNestedTable[tableIndex] === undefined)" class="pricing-table-section-content">
                            <DataTable :value="table.table_data.rows">
                                <Column v-for="column in table.table_data.columns" :key="column.id" :field="column.id" :header="column.title">
                                    <template #body="slotProps">
                                        <div style="width: 200px;" v-if="column.input_source === 'vendor'">
                                            <!-- Dropdown -->
                                            <Dropdown v-if="column.input_type === 3" 
                                                v-model="slotProps.data.data[column.id]" 
                                                :options="column.options" 
                                                optionLabel="option_value" 
                                                optionValue="option_value" 
                                                :class="['pricing-table-input', { 'mandatory-field': column.isMandatory && !slotProps.data.data[column.id] }]" 
                                                :required="column.isMandatory"
                                                :disabled="isInputDisabled"
                                                style="width: 170px;"
                                                placeholder="Select an option"
                                                />
                                            <!-- Text input for all other types -->
                                            <InputText style="width: 170px;" v-else v-model="slotProps.data.data[column.id]" :class="['pricing-table-input', { 'mandatory-field': column.isMandatory && !slotProps.data.data[column.id] }]" :placeholder="column.title" :required="column.isMandatory" :disabled="isInputDisabled" />
                                        </div>
                                        <div style="width: 200px;" v-else>
                                            {{ slotProps.data.data[column.id] }}
                                        </div>
                                    </template>
                                </Column>
                            </DataTable>
                        </div>
                        <div v-else-if="table.table_data.allowNestedTables && table.nestedTables && table.nestedTables.length > 0" class="pricing-table-section-content">
                            <div class="nested-table-header">
                                <Button icon="pi pi-trash" 
                                    class="p-button-danger p-button-text" 
                                    @click="removeNestedTable(tableIndex, activeNestedTable[tableIndex])"
                                    :disabled="isInputDisabled || activeNestedTable[tableIndex] === undefined" />
                            </div>
                            <DataTable v-if="activeNestedTable[tableIndex] !== undefined && table.nestedTables && table.nestedTables[activeNestedTable[tableIndex]]" :value="table.nestedTables[activeNestedTable[tableIndex]].table_data.rows">
                                <Column v-for="column in table.nestedTables[activeNestedTable[tableIndex]].table_data.columns" :key="column.id" :field="column.id" :header="column.title">
                                    <template #body="slotProps">
                                        <div style="width: 200px;" v-if="column.input_source === 'vendor'">
                                            <!-- Dropdown -->
                                            <Dropdown v-if="column.input_type === 3" 
                                                v-model="slotProps.data.data[column.id]" 
                                                :options="column.options" 
                                                optionLabel="option_value" 
                                                optionValue="option_value" 
                                                :class="['pricing-table-input', { 'mandatory-field': column.isMandatory && !slotProps.data.data[column.id] }]" 
                                                :required="column.isMandatory"
                                                :disabled="isInputDisabled"
                                                style="width: 170px;"
                                                placeholder="Select an option"
                                                />
                                            <!-- Text input for all other types -->
                                            <InputText style="width: 170px;" v-else v-model="slotProps.data.data[column.id]" :class="['pricing-table-input', { 'mandatory-field': column.isMandatory && !slotProps.data.data[column.id] }]" :placeholder="column.title" :required="column.isMandatory" :disabled="isInputDisabled" />
                                        </div>
                                        <div style="width: 200px;" v-else>
                                            {{ slotProps.data.data[column.id] }}
                                        </div>
                                    </template>
                                </Column>
                            </DataTable>
                            <div v-else class="no-nested-table">
                                <p>No nested table selected or table not found.</p>
                            </div>
                        </div>
                    </div>
                </TabPanel>
                <!-- Question Group Tabs -->
                <TabPanel v-for="(group, groupName) in groupedQuestions" :key="groupName">
                    <div class="pricing-table-section">
                        <p class="pricing-table-section-title">{{ groupName }}</p>
                        <div class="company-profile-form">
                            <div v-for="question in group" :key="question.id" class="company-profile-question" :data-question-id="question.id">
                                <label class="company-profile-label">
                                    {{ question.title }}
                                </label>
                                <!-- Long text -->
                                <Textarea v-if="question.idQuestionType === 1" v-model="question.response.answer" :placeholder="question.placeholder || 'Enter your answer'" :class="['company-profile-input', { 'mandatory-field': question.isMandatory && !question.response.answer }]" autoResize rows="3" :disabled="isInputDisabled" />
                                <!-- Single Choice (Dropdown) -->
                                <Dropdown v-else-if="question.idQuestionType === 2" v-model="question.response.answer" :options="question.options" optionLabel="optionValue" optionValue="id" :class="['company-profile-input', { 'mandatory-field': question.isMandatory && !question.response.answer }]" :disabled="isInputDisabled" />
                                <!-- Multiple Choice (MultiSelect) -->
                                <MultiSelect v-else-if="question.idQuestionType === 3" v-model="question.response.answer" :options="question.options" optionLabel="optionValue" optionValue="id" :class="['company-profile-input', { 'mandatory-field': question.isMandatory && (!question.response.answer || question.response.answer.length === 0) }]" :disabled="isInputDisabled" />
                                <!-- Short text -->
                                <InputText v-else-if="question.idQuestionType === 4" v-model="question.response.answer" :placeholder="question.placeholder || 'Enter your answer'" :class="['company-profile-input', { 'mandatory-field': question.isMandatory && !question.response.answer }]" :disabled="isInputDisabled" />
                                <!-- File Upload -->
                                <div v-else-if="question.idQuestionType === 5" class="file-upload-container">
                                    <div class="file-upload-wrapper">
                                        <input
                                            :key="question.fileInputKey || question.id"
                                            :ref="`fileInput_${question.id}`"
                                            type="file"
                                            :class="['file-upload-input', { 'mandatory-field': question.isMandatory && !isDocumentsArray(question.response.documents) }]"
                                            @change="onFileChange($event, question)"
                                            :disabled="uploadingFileId === question.id || isInputDisabled"
                                        />
                                        <div class="file-upload-label">
                                            <i class="pi pi-upload"></i>
                                            <span>{{ uploadingFileId ? 'Uploading...' : 'Choose File' }}</span>
                                        </div>
                                    </div>
                                    <div v-if="uploadingFileId" class="upload-progress">
                                        <ProgressBar mode="indeterminate" />
                                    </div>
                                    <div v-if="isDocumentsArray(question.response.documents)" class="file-name-container">
                                        <div class="file-name" @click="downloadFile(question.response.documents[0].name)" style="cursor: pointer;">
                                            {{ question.response.documents[0].name }}
                                        </div>
                                        <Button icon="pi pi-times" class="p-button-text p-button-danger remove-file-btn" @click="removeFile(question)" :disabled="isInputDisabled" />
                                    </div>
                                </div>
                                <!-- Dropdown single choice -->
                                <Dropdown v-else-if="question.idQuestionType === 6" v-model="question.response.answer" :options="question.options" optionLabel="optionValue" optionValue="id" :class="['company-profile-input', { 'mandatory-field': question.isMandatory && !question.response.answer }]" :disabled="isInputDisabled" />
                                <!-- Dropdown multiple choice -->
                                <MultiSelect v-else-if="question.idQuestionType === 7" v-model="question.response.answer" :options="question.options" optionLabel="optionValue" optionValue="id" :class="['company-profile-input', { 'mandatory-field': question.isMandatory && (!question.response.answer || question.response.answer.length === 0) }]" :disabled="isInputDisabled" />
                                <!-- Number -->
                                <InputNumber v-else-if="question.idQuestionType === 8" v-model="question.response.answer" :class="['company-profile-input', { 'mandatory-field': question.isMandatory && !question.response.answer }]" :disabled="isInputDisabled" />
                                <!-- Date -->
                                <Calendar v-else-if="question.idQuestionType === 9" 
                                    v-model="question.response.answer" 
                                    :class="['company-profile-input', { 'mandatory-field': question.isMandatory && !question.response.answer }]" 
                                    showIcon 
                                    :disabled="isInputDisabled"
                                    style="width: 150px;" />
                                <!-- Default: Text input -->
                                <InputText v-else v-model="question.response.answer" :placeholder="question.placeholder || 'Enter your answer'" :class="['company-profile-input', { 'mandatory-field': question.isMandatory && !question.response.answer }]" :disabled="isInputDisabled" />
                            </div>
                        </div>
                    </div>
                </TabPanel>
            </TabView>

            <Card>
                <template #content>
                    <div class="action-buttons">
                        <template v-if="!isRfpEnded">
                            <template v-if="showRetractButton">
                                <Button 
                                    label="Retract Response" 
                                    class="p-button-danger" 
                                    @click="handleRetractResponse" 
                                    :loading="retracting" />
                            </template>
                            <template v-else>
                                <Button 
                                    :label="'Save as Draft'" 
                                    class="p-button-primary"
                                    @click="handleSaveAsDraft" />
                                <Button 
                                    :label="isLastTab ? 'Submit Response' : 'Next'" 
                                    class="p-button-primary"
                                    @click="handleNext" />
                            </template>
                        </template>
                    </div>
                </template>
            </Card>
        </template>

        <!-- Terms and Conditions Dialog (unchanged) -->
        <Dialog v-model:visible="showTermsDialog" :modal="true" :closable="false" :style="{ width: '50vw' }"
            header="Terms and conditions">
            <div style="max-height: 50vh; overflow-y: auto;">
                <p>
                    By proceeding with receiving information in connection with this request for pricing, I understand
                    that any
                    information received is subject to the Marketplace Partner Agreement signed between ArenaCX and the
                    marketplace partner on behalf of whom I work or am engaged (the "MPPA"). Accordingly, I understand
                    that any
                    potential or prospective customer or marketplace member revealed or provided as part of this request
                    for
                    pricing represents ArenaCX notifying me that this potential or prospective customer or marketplace
                    member
                    shall be considered a Qualified Sales Opportunity (QSO) and such information (including the name of
                    such
                    potential or prospective customer or marketplace member) shall (i) constitute Confidential
                    Information under
                    the MPPA and (ii) shall be subject in all respects to the terms and conditions of the MPPA,
                    including,
                    without limitation, the non-circumvention provisions in Section 2 of the MPPA.
                </p>
                <div class="p-field-checkbox">
                    <Checkbox v-model="termsAccepted" :binary="true" inputId="termsCheckbox" />
                    <label for="termsCheckbox">I agree and accept the above terms and condition of use</label>
                </div>
            </div>
            <template #footer>
                <Button label="Continue" :disabled="!termsAccepted" @click="onTermsAccepted" />
            </template>
        </Dialog>

        <!-- Add Retract Confirmation Dialog -->
        <Dialog v-model:visible="showRetractDialog" :modal="true" :closable="false" :style="{ width: '30vw' }"
            header="Confirm Retraction">
            <div class="retract-confirmation">
                <p>Are you sure you want to retract your response? This action cannot be undone.</p>
            </div>
            <template #footer>
                <Button label="Cancel" class="p-button-text" @click="showRetractDialog = false" />
                <Button label="Retract" class="p-button-danger" @click="confirmRetract" :loading="retracting" />
            </template>
        </Dialog>

        <!-- Add Decline Confirmation Dialog -->
        <Dialog v-model:visible="showDeclineDialog" :modal="true" :closable="false" :style="{ width: '30vw' }"
            header="Confirm Decline">
            <div class="decline-confirmation">
                <p>Are you sure you want to decline this opportunity? This action cannot be undone.</p>
            </div>
            <template #footer>
                <Button label="Cancel" class="p-button-text" @click="showDeclineDialog = false" />
                <Button label="Decline" class="p-button-danger" @click="confirmDecline" :loading="declining" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import Dialog from 'primevue/dialog';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import { useRoute } from 'vue-router';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Toast from 'primevue/toast';
import ProgressBar from 'primevue/progressbar';
import Sidebar from 'primevue/sidebar';
import Messaging from '@/components/partnerOpportunity/Messaging.vue';
import PartnerOpportunityService from '../service/PartnerOpportunityService';

export default {
    name: 'Opportunity',
    components: { Dialog, Checkbox, Button, InputText, TabView, TabPanel, Toast, ProgressBar, Sidebar, Messaging },
    data() {
        return {
            opportunityId: null,
            loading: true,
            showTermsDialog: false,
            termsAccepted: false,
            showPricingTable: false,
            active: 0,
            baseRateActive: 0,
            rfpEndTime: null,
            countdown: {
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0
            },
            countdownInterval: null,
            partnerOpportunityService: new PartnerOpportunityService(),
            opportunityDetails: {
                questions: [] // Initialize with empty questions array
            },
            uploadingFileId: null, // Track which file is currently uploading
            retracting: false, // Track retract operation status
            showRetractDialog: false, // Track retract confirmation dialog visibility
            declining: false, // Track decline operation status
            showDeclineDialog: false, // Track decline confirmation dialog visibility
            activeNestedTable: {}, // Track active nested table for each parent table
            showMessageSidebar: false,
            selectedMessage: null,
            isCreatingMessage: false,
            editingNestedTableName: {
                tableIndex: null,
                nestedIndex: null,
                newName: ''
            }
        };
    },
    async created() {
        const route = useRoute();
        this.opportunityId = route.query.id;
        console.log("Component created with ID:", this.opportunityId);
        await this.getTermsAndConditionsStatus();
        await this.getOpportunityDetails();
        this.loading = false;
        this.startCountdown();
    },
    mounted() {
        console.log("Component mounted");
        console.log("Initial opportunityDetails:", this.opportunityDetails);
        console.log("Initial groupedQuestions:", this.groupedQuestions);
    },
    beforeUnmount() {
        // Clean up the interval when component is destroyed
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
    },
    methods: {
        // Helper method to safely check if documents is a valid array
        isDocumentsArray(documents) {
            return documents && Array.isArray(documents) && documents.length > 0;
        },

        // Test method to verify documents handling
        testDocumentsHandling() {
            console.log('Testing documents handling:');
            console.log('Empty object {}:', this.isDocumentsArray({}));
            console.log('Empty array []:', this.isDocumentsArray([]));
            console.log('Array with items:', this.isDocumentsArray([{name: 'test'}]));
            console.log('null:', this.isDocumentsArray(null));
            console.log('undefined:', this.isDocumentsArray(undefined));
        },

        // Format date
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('default', { month: 'short' });
            const year = date.getFullYear().toString().slice(-2);
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
        },
        // Get the opportunity details
        async getOpportunityDetails() {
            if (this.termsAccepted) {
                try {
                    const response = await this.partnerOpportunityService.getOpportunityDetails({
                        encryptionId: this.opportunityId
                    });
                    if (response.status === 200) {
                        console.log("Get Opportunity Details Response:", response);
                        this.opportunityDetails = response.data;
                        
                        // Process the answers for different question types
                        if (this.opportunityDetails?.questions) {
                            await this.processQuestions();
                        }

                        if (this.opportunityDetails?.pricing_data) {
                            await this.transformPricingData();
                        }
                        
                        console.log("Set opportunityDetails:", this.opportunityDetails);
                        // Force a re-render of the company profile section
                        await this.$nextTick();
                        console.log("After nextTick - opportunityDetails:", this.opportunityDetails);
                        console.log("After nextTick - groupedQuestions:", this.groupedQuestions);
                        
                        this.rfpEndTime = new Date(response.data.end_date);
                    }
                } catch (error) {
                    console.error('Error fetching opportunity details:', error);
                }
            } else {
                console.log("Terms not accepted yet");
            }
        },

        // Get the terms and conditions status
        async getTermsAndConditionsStatus() {
            try {
                const response = await this.partnerOpportunityService.getTermsAndConditionsStatus({
                    encryptionId: this.opportunityId
                });
                if (response.status === 200) {
                    // Show dialog if terms are not accepted
                    this.showTermsDialog = !response.data.termsAccepted;
                    this.termsAccepted = response.data.termsAccepted;
                }
            } catch (error) {
                console.error('Error fetching terms and conditions status:', error)
                this.showTermsDialog = true; // Show dialog on error to ensure user accepts terms
            }
        },

        // Update the terms and conditions
        async updateTermsAndConditions() {
            try {
                const response = await this.partnerOpportunityService.updateTermsAndConditions({
                    encryptionId: this.opportunityId
                });
                return response;
            } catch (error) {
                console.error('Error updating terms and conditions:', error);
                throw error; // Propagate error to handle it in the calling method
            }
        },

        // Handle the terms and conditions acceptance
        async onTermsAccepted() {
            try {
                const response = await this.updateTermsAndConditions();
                if (response && response.status === 200) {
                    this.termsAccepted = true;
                    this.showTermsDialog = false;
                    // Call getOpportunityDetails after accepting terms
                    await this.getOpportunityDetails();
                    // Start the countdown timer after loading opportunity details
                    this.startCountdown();
                } else {
                    // If update failed, keep dialog open
                    this.showTermsDialog = true;
                }
            } catch (error) {
                console.error('Error in terms acceptance:', error);
                this.showTermsDialog = true; // Keep dialog open on error
            }
        },

        // Start the countdown timer
        startCountdown() {
            this.updateCountdown();
            this.countdownInterval = setInterval(this.updateCountdown, 1000);
        },

        // Update the countdown timer
        updateCountdown() {
            const now = new Date();
            const diff = this.rfpEndTime - now;

            if (diff <= 0) {
                // If time is up, clear the interval and set countdown to null
                clearInterval(this.countdownInterval);
                this.countdown = null;
                return;
            }

            // Calculate time units
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            this.countdown = { days, hours, minutes, seconds };
        },

        // Handle the participation
        async onParticipate() {
            try {
                const response = await this.partnerOpportunityService.participate({
                    encryptionId: this.opportunityId
                });
                
                if (response.status === 200) {
                    // Show pricing table only after successful participation
                    this.showPricingTable = true;
                    
                    // Refresh opportunity details to get updated status
                    await this.getOpportunityDetails();
                } else {
                    // Handle error case - you might want to show an error message
                    console.error('Failed to participate:', response);
                }
            } catch (error) {
                console.error('Error participating:', error);
                // Handle error case - you might want to show an error message
            }
        },

        // Toggle the pricing table
        togglePricingTable() {
            this.showPricingTable = true;
        },

        // Handle the next button
        async handleNext() {
            // If we're in pricing tables section
            if (this.active < this.opportunityDetails?.pricing_data.length) {
                // Validate current table's mandatory fields
                const currentTable = this.opportunityDetails.pricing_data[this.active];
                let missingFields = [];
                
                    currentTable.table_data.rows.forEach((row, rowIndex) => {
                        currentTable.table_data.columns.forEach(column => {
                            if (column.isMandatory && column.input_source === 'vendor' && !row.data[column.id] && !currentTable.table_data.allowNestedTables) {
                                missingFields.push({
                                    table: currentTable.table_name,
                                    row: rowIndex + 1,
                                    column: column.title
                                });
                                row.data[column.id] = ''; // Ensure empty string for validation styling
                                console.log("Missing field:", column.title);
                            }
                        });
                    });

                if (missingFields.length > 0) {
                    console.log('Missing mandatory fields:', missingFields);
                    
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Required Fields Missing',
                        detail: 'Please fill in all required fields before proceeding.',
                        life: 3000
                    });
                    return;
                }

                this.active++;
            } else {
                // We're in question groups section
                const currentGroupIndex = this.active - this.opportunityDetails?.pricing_data.length;
                const currentGroup = Object.values(this.groupedQuestions)[currentGroupIndex];
                
                // Validate current question group
                let missingQuestions = [];
                currentGroup.forEach(question => {
                    if (question.isMandatory) {
                        const isFileUpload = question.idQuestionType === 5;
                        const hasNoFile = !this.isDocumentsArray(question.response.documents);
                        const hasNoAnswer = !question.response.answer;

                        if (isFileUpload && hasNoFile) {
                            missingQuestions.push({
                                question: question.title,
                                type: 'file'
                            });
                            question.response.answer = '';
                        } else if (!isFileUpload && hasNoAnswer) {
                            missingQuestions.push({
                                question: question.title,
                                type: 'text'
                            });
                            question.response.answer = '';
                        }
                    }
                });

                if (missingQuestions.length > 0) {
                    console.log('Missing mandatory questions:', missingQuestions);
                    
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Required Fields Missing',
                        detail: 'Please fill in all required fields before proceeding.',
                        life: 3000
                    });
                    return;
                }

                // Check if this is the last group
                const isLastGroup = currentGroupIndex === Object.keys(this.groupedQuestions).length - 1;
                
                if (isLastGroup) {
                    // Handle submission
                    const submissionData = this.constructSubmissionData();
                    const payload = {
                        encryptionId: this.opportunityId,
                        submissionData: submissionData
                    };
                    
                    try {
                        const response = await this.partnerOpportunityService.submitOpportunityResponse(payload);
                        console.log("Submission response:", response);
                        
                        if (response.status === 200) {
                            this.$toast.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'Your response has been submitted successfully.',
                                life: 3000
                            });
                            await this.getOpportunityDetails();
                            // You might want to redirect or show a success page here
                        }
                    } catch (error) {
                        console.error('Error submitting response:', error);
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to submit response. Please try again.',
                            life: 3000
                        });
                    }
                } else {
                    // Move to next tab
                    this.active++;
                }
            }
        },

        async handleSaveAsDraft() {
            console.log("Saving as draft");
            const submissionData = this.constructSubmissionData();
            const payload = {
                encryptionId: this.opportunityId,
                submissionData: submissionData
            };

            console.log("Draft payload: >", payload);
            
            try {
                const response = await this.partnerOpportunityService.saveAsDraft(payload);
                console.log("Draft save response:", response);
                
                if (response.status === 200) {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Your response has been saved as draft successfully.',
                        life: 3000
                    });
                    await this.getOpportunityDetails();
                }
            } catch (error) {
                console.error('Error saving draft:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to save draft. Please try again.',
                    life: 3000
                });
            }
        },

        // Toggle the intro
        async toggleIntro() {
            await this.getOpportunityDetails()
            this.showPricingTable = false;
            this.active = 0;
        },

        async downloadAttachment(attachment) {
            console.log("Downloading attachment", attachment);
            try {
                const response = await this.partnerOpportunityService.downloadAttachment({
                    fileName: attachment.fileName
                });
                
                if (response.status === 200) {
                    // Get the signed URL from the response
                    const signedUrl = response.data.downloadURL;
                    
                    // Create a temporary link element
                    const link = document.createElement('a');
                    link.href = signedUrl;
                    link.download = attachment.fileName;
                    // Append to body, click and remove
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (error) {
                console.error('Error downloading attachment:', error);
                // You might want to show an error message to the user here
            }
        },

        formatTableData(table) {
            // Transform the rows data into a format suitable for DataTable
            return table.table_data.rows.map(row => {
                const formattedRow = {};
                // Add all column values to the row
                table.table_data.columns.forEach(column => {
                    formattedRow[column.id] = row.data[column.id] || '';
                });
                return formattedRow;
            });
        },

        async onFileChange(event, question) {
            const file = event.target.files[0];
            console.log("file >>", file);
            if (file) {
                // Check file size (5MB = 5 * 1024 * 1024 bytes)
                const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                if (file.size > maxSize) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'File Too Large',
                        detail: 'File size should not exceed 5MB. Please choose a smaller file.',
                        life: 3000
                    });
                    // Clear the file input
                    event.target.value = '';
                    return;
                }

                try {
                    // Set uploading state for this question
                    this.uploadingFileId = question.id;

                    // Get pre-signed URL for the file
                    const preSignedUrlResponse = await this.partnerOpportunityService.getPreSignedUrlForAttachment({
                        fileName: file.name
                    });

                    console.log("Pre-signed URL response ==>", preSignedUrlResponse, file);

                    if (preSignedUrlResponse.status === 200) {
                        // Upload file using pre-signed URL
                        const uploadResponse = await fetch(preSignedUrlResponse.data.uploadURL, {
                            method: 'PUT',
                            body: file,
                            headers: {
                                'Content-Type': file.type
                            }
                        });

                        console.log("Upload response ==>", uploadResponse);

                        if (uploadResponse.ok) {
                            // Store file information in question response
                            question.response.documents = [{
                                name: file.name,
                                fileUrl: preSignedUrlResponse.data.uploadURL.split('?')[0], // Get the base URL without query parameters
                                key: preSignedUrlResponse.data.Key
                            }];
                            question.response.questionTypeId = question.idQuestionType;
                        } else {
                            this.$toast.add({
                                severity: 'error',
                                summary: 'Upload Failed',
                                detail: 'Failed to upload file. Please try again.',
                                life: 3000
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error uploading file:', error);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Upload Failed',
                        detail: 'An error occurred while uploading the file. Please try again.',
                        life: 3000
                    });
                } finally {
                    // Clear uploading state
                    this.uploadingFileId = null;
                }
            }
        },

        removeFile(question) {
            question.response.documents = [];
            console.log("question.response.documents >>", question.response.documents);
            
            // Clear the file input value to allow re-selecting the same file
            this.$nextTick(() => {
                // Find the file input within the same question container
                const questionElement = document.querySelector(`[data-question-id="${question.id}"]`);
                if (questionElement) {
                    const fileInput = questionElement.querySelector('.file-upload-input');
                    if (fileInput) {
                        fileInput.value = '';
                    }
                }
            });
        },

        async downloadFile(fileName) {
            try {
                const response = await this.partnerOpportunityService.downloadAttachment({
                    fileName: fileName
                });
                
                if (response.status === 200) {
                    // Get the signed URL from the response
                    const signedUrl = response.data.downloadURL;
                    
                    // Fetch the file
                    const fileResponse = await fetch(signedUrl);
                    const blob = await fileResponse.blob();
                    
                    // Create object URL
                    const url = window.URL.createObjectURL(blob);
                    
                    // Create a temporary link element
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    
                    // Append to body, click and remove
                    document.body.appendChild(link);
                    link.click();
                    
                    // Clean up
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }
            } catch (error) {
                console.error('Error downloading file:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Download Failed',
                    detail: 'An error occurred while downloading the file. Please try again.',
                    life: 3000
                });
            }
        },

        constructSubmissionData() {
            // Format questions data
            const answers = this.opportunityDetails.questions.map(question => {
                const answer = {
                    questionId: question.id,
                    questionTypeId: question.idQuestionType,
                    documents: []
                };

                // Handle different question types
                switch (question.idQuestionType) {
                    case 5: // File Upload
                        if (this.isDocumentsArray(question.response.documents)) {
                            answer.documents = question.response.documents.map(doc => ({
                                documentId: doc.key,
                                documentName: doc.name
                            }));
                        }
                        break;

                    case 2: // Single Choice
                    case 6: // Dropdown single choice
                        if (question.response.answer) {
                            const option = question.options.find(opt => opt.id === question.response.answer);
                            answer.answer = option ? option.optionValue : '';
                        }
                        break;

                    case 3: // Multiple Choice
                    case 7: // Dropdown multiple choice
                        if (question.response.answer && Array.isArray(question.response.answer)) {
                            answer.answer = question.response.answer
                                .map(id => {
                                    const option = question.options.find(opt => opt.id === id);
                                    return option ? option.optionValue : '';
                                })
                                .filter(value => value !== '');
                        }
                        break;

                    case 1: // Long text
                    case 4: // Short text
                    case 8: // Number
                    case 9: // Date
                        answer.answer = question.response.answer;
                        break;
                }

                return answer;
            });

            // Format pricing data - flatten nested tables into main tables
            const pricing_data = this.opportunityDetails.pricing_data.reduce((acc, table) => {
                // Add main table
                acc.push({
                    table_name: table.table_name,
                    table_data: {
                        id: table.table_data.id,
                        rows: table.table_data.rows,
                        columns: table.table_data.columns,
                        sort_order: table.table_data.sort_order,
                        hasNestedTables: table.table_data.hasNestedTables,
                        allowNestedTables: table.table_data.allowNestedTables !== undefined ? table.table_data.allowNestedTables : false
                    }
                });

                // Add nested tables as main tables with parent reference
                if (table.nestedTables && table.nestedTables.length > 0) {
                    table.nestedTables.forEach(nestedTable => {
                        acc.push({
                            table_name: nestedTable.table_name,
                            table_data: {
                                rows: nestedTable.table_data.rows,
                                columns: nestedTable.table_data.columns,
                                sort_order: nestedTable.table_data.sort_order,
                                parent_table_id: table.table_data.id
                            }
                        });
                    });
                }

                return acc;
            }, []);

            return {
                answers,
                pricing_data
            };
        },

        handleRetractResponse() {
            this.showRetractDialog = true;
        },

        handleDecline() {
            this.showDeclineDialog = true;
        },

        async confirmDecline() {
            try {
                this.declining = true;
                const response = await this.partnerOpportunityService.decline({
                    encryptionId: this.opportunityId
                });
                
                if (response.status === 200) {
                    // Close the dialog
                    this.showDeclineDialog = false;
                    // Refresh opportunity details to get updated status
                    await this.getOpportunityDetails();
                    
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Opportunity has been declined successfully.',
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('Error declining opportunity:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to decline opportunity. Please try again.',
                    life: 3000
                });
            } finally {
                this.declining = false;
            }
        },

        async confirmRetract() {
            try {
                this.retracting = true;
                const response = await this.partnerOpportunityService.retractResponse({
                    encryptionId: this.opportunityId
                });
                
                if (response.status === 200) {
                    // Close the dialog
                    this.showRetractDialog = false;
                    // Refresh opportunity details to get updated status
                    await this.getOpportunityDetails();
                    
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Response has been retracted successfully.',
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('Error retracting response:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to retract response. Please try again.',
                    life: 3000
                });
            } finally {
                this.retracting = false;
            }
        },

        addNestedTable(parentTableIndex) {
            if (!this.opportunityDetails.pricing_data[parentTableIndex].nestedTables) {
                this.opportunityDetails.pricing_data[parentTableIndex].nestedTables = [];
            }

            const parentTable = this.opportunityDetails.pricing_data[parentTableIndex];
            
            // Create a deep copy of the parent table including all data
            const newNestedTable = {
                table_name: `${parentTable.table_name} - ${parentTable.nestedTables.length + 1}`,
                table_data: {
                    rows: parentTable.table_data.rows.map(row => ({
                        id: `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        data: JSON.parse(JSON.stringify(row.data)), // Deep copy of row data
                        sort_order: row.sort_order
                    })),
                    columns: JSON.parse(JSON.stringify(parentTable.table_data.columns)), // Deep copy of columns
                    sort_order: parentTable.table_data.sort_order,
                    hasNestedTables: parentTable.table_data.hasNestedTables
                }
            };

            parentTable.nestedTables.push(newNestedTable);
            console.log("parentTable >>", parentTable);
            parentTable.table_data.hasNestedTables = true; 
            this.activeNestedTable[parentTableIndex] = parentTable.nestedTables.length - 1;
            
            // Automatically start editing the name of the newly created table
            this.$nextTick(() => {
                this.startEditNestedTableName(parentTableIndex, parentTable.nestedTables.length - 1);
            });
        },

        removeNestedTable(parentTableIndex, nestedTableIndex) {
            const parentTable = this.opportunityDetails.pricing_data[parentTableIndex];
            
            // Safety check: ensure parent table and nested tables exist
            if (!parentTable || !parentTable.nestedTables || nestedTableIndex === undefined) {
                console.warn('Invalid nested table removal attempt:', { parentTableIndex, nestedTableIndex });
                return;
            }
            
            // Safety check: ensure the nested table index is valid
            if (nestedTableIndex < 0 || nestedTableIndex >= parentTable.nestedTables.length) {
                console.warn('Invalid nested table index:', nestedTableIndex);
                return;
            }
            
            parentTable.nestedTables.splice(nestedTableIndex, 1);

            console.log("parentTable >>", parentTable);
            // Update hasNestedTables based on remaining nested tables
            parentTable.table_data.hasNestedTables = parentTable.nestedTables.length > 0;

            // Reset active nested table if needed
            if (this.activeNestedTable[parentTableIndex] !== undefined) {
                if (parentTable.nestedTables.length === 0) {
                    // No nested tables left, remove the active state
                    delete this.activeNestedTable[parentTableIndex];
                } else if (this.activeNestedTable[parentTableIndex] >= parentTable.nestedTables.length) {
                    // Current active index is now out of bounds, reset to 0
                    this.activeNestedTable[parentTableIndex] = 0;
                }
            }
        },

        // New method to process questions
        async processQuestions() {
            return new Promise((resolve) => {
                this.opportunityDetails.questions.forEach(question => {
                    if (question.response) {
                        if (question.idQuestionType === 2) {
                            // For single choice, convert array of one string to option ID
                            if (question.response.answer) {
                                const answerText = Array.isArray(question.response.answer) 
                                    ? question.response.answer[0] 
                                    : question.response.answer;
                                const option = question.options.find(opt => opt.optionValue === answerText);
                                question.response.answer = option ? option.id : null;
                            }
                        } else if (question.idQuestionType === 3) {
                            // For multiple choice, convert array of strings to option IDs
                            if (question.response.answer) {
                                const selectedOptions = question.response.answer.map(answerText => {
                                    const option = question.options.find(opt => opt.optionValue === answerText);
                                    return option ? option.id : null;
                                }).filter(id => id !== null);
                                
                                question.response.answer = selectedOptions;
                            }
                        } else if (question.idQuestionType === 5) {
                            // For file upload, convert documents object to array format
                            if (question.response.documents) {
                                try {
                                    // Handle both string and object formats
                                    const docs = typeof question.response.documents === 'string' 
                                        ? JSON.parse(question.response.documents)
                                        : question.response.documents;

                                    // Check if it's an empty object (no properties)
                                    if (typeof docs === 'object' && docs !== null && !Array.isArray(docs) && Object.keys(docs).length === 0) {
                                        question.response.documents = [];
                                    } else {
                                        // Convert to array format if it's a single document
                                        question.response.documents = Array.isArray(docs) 
                                            ? docs 
                                            : [{
                                                key: docs.documentId,
                                                name: docs.documentName,
                                                fileUrl: docs.documentId
                                            }];
                                    }
                                } catch (e) {
                                    console.error('Error parsing documents:', e);
                                    question.response.documents = [];
                                }
                            } else {
                                question.response.documents = [];
                            }
                        } else if (question.idQuestionType === 9) {
                            // For date type, convert string to Date object
                            if (question.response.answer) {
                                question.response.answer = new Date(question.response.answer);
                            }
                        }
                    }
                });
                resolve();
            });
        },

        // New method to transform pricing data
        async transformPricingData() {
            return new Promise((resolve) => {
                // Transform flat pricing data into nested structure
                const pricingDataMap = new Map();
                const nestedPricingData = [];
                
                // Store current activeNestedTable state before transformation
                const currentActiveNestedTable = { ...this.activeNestedTable };
                const tableIndexMap = new Map(); // Map to track old table indices to new ones

                // First pass: Create map of all tables
                this.opportunityDetails.pricing_data.forEach((table, index) => {
                    console.log("Creating map for table:", table.table_name, "ID:", table.table_data.id);
                    pricingDataMap.set(table.table_data.id, {
                        ...table,
                        nestedTables: []
                    });
                    tableIndexMap.set(index, table.table_data.id);
                });

                // Second pass: Build nested structure
                this.opportunityDetails.pricing_data.forEach(table => {
                    console.log("Processing table:", table.table_name, "Parent ID:", table.table_data.parent_table_id);
                    if (table.table_data.parent_table_id) {
                        // This is a nested table
                        const parentTable = pricingDataMap.get(table.table_data.parent_table_id);
                        if (parentTable) {
                            console.log("Adding nested table to parent:", parentTable.table_name);
                            parentTable.nestedTables.push(table);
                        } else {
                            console.log("Parent table not found for:", table.table_name);
                        }
                    } else {
                        // This is a parent table
                        console.log("Adding parent table:", table.table_name);
                        nestedPricingData.push(pricingDataMap.get(table.table_data.id));
                    }
                });

                this.opportunityDetails.pricing_data = nestedPricingData;
                
                // Third pass: Auto-create nested tables for parent tables that allow them but have none
                nestedPricingData.forEach((table, tableIndex) => {
                    if (table.table_data.allowNestedTables && (!table.nestedTables || table.nestedTables.length === 0)) {
                        console.log("Auto-creating nested table for:", table.table_name);
                        
                        // Create a nested table from the parent table
                        const autoNestedTable = {
                            table_name: `${table.table_name} - 1`,
                            table_data: {
                                rows: table.table_data.rows.map(row => ({
                                    id: `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                                    data: JSON.parse(JSON.stringify(row.data)), // Deep copy of row data
                                    sort_order: row.sort_order
                                })),
                                columns: JSON.parse(JSON.stringify(table.table_data.columns)), // Deep copy of columns
                                sort_order: table.table_data.sort_order,
                                hasNestedTables: false,
                                parent_table_id: table.table_data.id
                            }
                        };
                        
                        // Initialize nestedTables array if it doesn't exist
                        if (!table.nestedTables) {
                            table.nestedTables = [];
                        }
                        
                        // Add the auto-created nested table
                        table.nestedTables.push(autoNestedTable);
                        table.table_data.hasNestedTables = true;
                        
                        // Set this as the active nested table
                        this.activeNestedTable[tableIndex] = 0;
                        
                        console.log("Auto-created nested table:", autoNestedTable.table_name);
                    }
                });
                
                // Restore activeNestedTable state with new indices
                const newActiveNestedTable = {};
                nestedPricingData.forEach((table, newIndex) => {
                    // Find the old index for this table
                    const oldIndex = Array.from(tableIndexMap.entries()).find(([oldIdx, tableId]) => 
                        tableId === table.table_data.id
                    )?.[0];
                    
                    if (oldIndex !== undefined && currentActiveNestedTable[oldIndex] !== undefined) {
                        // Check if the nested table index is still valid
                        const nestedTableIndex = currentActiveNestedTable[oldIndex];
                        if (table.nestedTables && nestedTableIndex < table.nestedTables.length) {
                            newActiveNestedTable[newIndex] = nestedTableIndex;
                        }
                    }
                });
                
                // Merge with auto-created active nested tables
                this.activeNestedTable = { ...newActiveNestedTable, ...this.activeNestedTable };
                
                // Clean up any invalid states
                this.cleanupActiveNestedTableState();
                
                console.log("Final nested structure:", this.opportunityDetails.pricing_data);
                console.log("Restored activeNestedTable:", this.activeNestedTable);
                resolve();
            });
        },

        // Simple method to clean up invalid activeNestedTable states
        cleanupActiveNestedTableState() {
            const validState = {};
            this.opportunityDetails.pricing_data.forEach((table, tableIndex) => {
                if (this.activeNestedTable[tableIndex] !== undefined) {
                    const nestedIndex = this.activeNestedTable[tableIndex];
                    if (table.nestedTables && nestedIndex >= 0 && nestedIndex < table.nestedTables.length) {
                        validState[tableIndex] = nestedIndex;
                    }
                }
            });
            this.activeNestedTable = validState;
        },

        openMessageSidebar() {
            this.selectedMessage = { title: 'ArenaCX', body: 'This is a sample message.' };
            this.showMessageSidebar = true;
        },
        closeMessageSidebar() {
            this.showMessageSidebar = false;
            this.selectedMessage = null;
            this.isCreatingMessage = false;
        },
        startNewMessage() {
            this.isCreatingMessage = true;
        },

        // Validation methods for tab navigation
        validateTabNavigation(oldActive, newActive) {
            // Store the previous active tab to revert if validation fails
            const previousActive = oldActive;
            
            // Validate the current tab before allowing navigation
            if (this.validateCurrentTab(oldActive)) {
                // Validation passed, allow navigation
                return true;
            } else {
                // Validation failed, revert to previous tab
                this.$nextTick(() => {
                    this.active = previousActive;
                });
                return false;
            }
        },

        validateCurrentTab(tabIndex) {
            const pricingDataLength = this.opportunityDetails?.pricing_data?.length || 0;
            
            if (tabIndex < pricingDataLength) {
                // This is a pricing table tab
                return this.validatePricingTable(tabIndex);
            } else {
                // This is a question group tab
                const questionGroupIndex = tabIndex - pricingDataLength;
                return this.validateQuestionGroup(questionGroupIndex);
            }
        },

        validatePricingTable(tableIndex) {
            const table = this.opportunityDetails.pricing_data[tableIndex];
            if (!table) return true;

            let missingFields = [];
            
            // Always validate main table
            table.table_data.rows.forEach((row, rowIndex) => {
                table.table_data.columns.forEach(column => {
                    if (column.isMandatory && column.input_source === 'vendor' && !row.data[column.id] && !table.table_data.allowNestedTables) {
                        console.log("Missing field Main >>:", column.title);
                        missingFields.push({
                            table: table.table_name,
                            row: rowIndex + 1,
                            column: column.title
                        });
                    }
                });
            });

            // Validate all nested tables if they exist
            if (table.nestedTables && table.nestedTables.length > 0) {
                table.nestedTables.forEach((nestedTable) => {
                    nestedTable.table_data.rows.forEach((row, rowIndex) => {
                        nestedTable.table_data.columns.forEach(column => {
                            if (column.isMandatory && column.input_source === 'vendor' && !row.data[column.id]) {
                                console.log("Missing field Nested >>:", column.title, "in table:", nestedTable.table_name);
                                missingFields.push({
                                    table: nestedTable.table_name,
                                    row: rowIndex + 1,
                                    column: column.title
                                });
                            }
                        });
                    });
                });
            }

            if (missingFields.length > 0) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Required Fields Missing',
                    detail: 'Please fill in all required fields before proceeding.',
                    life: 3000
                });
                return false;
            }

            return true;
        },

        validateQuestionGroup(groupIndex) {
            const groups = Object.values(this.groupedQuestions);
            const currentGroup = groups[groupIndex];
            
            if (!currentGroup) return true;

            let missingQuestions = [];
            
            currentGroup.forEach(question => {
                if (question.isMandatory) {
                    const isFileUpload = question.idQuestionType === 5;
                    const hasNoFile = !this.isDocumentsArray(question.response.documents);
                    const hasNoAnswer = !question.response.answer;

                    if (isFileUpload && hasNoFile) {
                        missingQuestions.push({
                            question: question.title,
                            type: 'file'
                        });
                    } else if (!isFileUpload && hasNoAnswer) {
                        missingQuestions.push({
                            question: question.title,
                            type: 'text'
                        });
                    }
                }
            });

            if (missingQuestions.length > 0) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Required Fields Missing',
                    detail: 'Please fill in all required fields before proceeding.',
                    life: 3000
                });
                return false;
            }

            return true;
        },

        startEditNestedTableName(tableIndex, nestedIndex) {
            const nestedTable = this.opportunityDetails.pricing_data[tableIndex].nestedTables[nestedIndex];
            this.editingNestedTableName = { 
                tableIndex, 
                nestedIndex, 
                newName: nestedTable.table_name 
            };
            
            // Focus the input after it's rendered
            this.$nextTick(() => {
                if (this.$refs.nestedTableNameInput) {
                    this.$refs.nestedTableNameInput[0].focus();
                    this.$refs.nestedTableNameInput[0].select();
                }
            });
        },

        confirmEditNestedTableName(tableIndex, nestedIndex) {
            const newName = this.editingNestedTableName.newName.trim();
            
            if (!newName) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Invalid Name',
                    detail: 'Table name cannot be empty.',
                    life: 3000
                });
                return;
            }
            
            // Check if name already exists (excluding current table)
            const existingNames = this.opportunityDetails.pricing_data[tableIndex].nestedTables
                .map((table, index) => index !== nestedIndex ? table.table_name : null)
                .filter(name => name !== null);
            
            if (existingNames.includes(newName)) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Duplicate Name',
                    detail: 'A table with this name already exists.',
                    life: 3000
                });
                return;
            }
            
            // Update the table name
            this.opportunityDetails.pricing_data[tableIndex].nestedTables[nestedIndex].table_name = newName;
            
            // Reset editing state
            this.editingNestedTableName = { tableIndex: null, nestedIndex: null, newName: '' };
            
            this.$toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Table name updated successfully.',
                life: 2000
            });
        },

        cancelEditNestedTableName() {
            this.editingNestedTableName = { tableIndex: null, nestedIndex: null, newName: '' };
        }
    },
    computed: {
        groupedQuestions() {
            console.log("Computing groupedQuestions - opportunityDetails:", this.opportunityDetails);
            if (!this.opportunityDetails?.questions) {
                console.log("No questions found in opportunityDetails");
                return {};
            }
            
            const groups = this.opportunityDetails.questions.reduce((groups, question) => {
                const groupName = question.questionGroup || 'Other';
                if (!groups[groupName]) {
                    groups[groupName] = [];
                }
                groups[groupName].push(question);
                return groups;
            }, {});
            
            console.log("Computed groups:", groups);
            return groups;
        },
        isLastTab() {
            const totalTabs = (this.opportunityDetails?.pricing_data?.length || 0) + Object.keys(this.groupedQuestions).length;
            return this.active === totalTabs - 1;
        },
        isRfpEnded() {
            return this.rfpEndTime && new Date() > this.rfpEndTime;
        },
        isInputDisabled() {
            return this.isRfpEnded || this.opportunityDetails?.vendor_status_id === 10;
        },
        showRetractButton() {
            return this.opportunityDetails?.vendor_status_id === 10;
        }
    },
    watch: {
        opportunityDetails: {
            handler(newVal) {
                console.log("opportunityDetails changed:", newVal);
                console.log("Questions:", newVal?.questions);
            },
            deep: true
        },
        active: {
            handler(newActive, oldActive) {
                // Only validate when moving forward (not backward)
                if (newActive > oldActive && oldActive !== undefined) {
                    this.validateTabNavigation(oldActive, newActive);
                }
            }
        }
    }
};
</script>

<style scoped>
.message-btn-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
}

.opportunity-container {
    margin: 2rem auto;
    padding: 1rem;
}


.rfp-title {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 6px;
}

.header-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 1.5rem 1rem 1rem 1rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.header-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.header-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
}

.opportunity-id-badge,
.timer-badge,
.date-badge {
    background: #E5F0EE;
    color: #245B5F;
    border-radius: 4px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
}

.timer-badge {
    background: #E5EDDA;
    color: #333333;
}

.timer-badge.ended {
    background: #FFE5E5;
    color: #D32F2F;
}

.date-badge-container {
    display: flex;
    gap: 8px;
}

.date-badge {
    background: #E5F0EE;
    color: #333333;
}


.intro-section,
.attachments-section,
.links-section {
    padding: 1rem;
}

.intro-title-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 1rem;
    border-bottom: 1px solid #E6E6E6;
}

.intro-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 6px;
    color: #222222;
}

.intro-subtitle {
    font-size: 14px;
    font-weight: 500;
    color: #555555
}

.intro-section,
.links-section,
.attachments-section {
    margin-bottom: 1.5rem;
}

.intro-content {
    font-size: 1rem;
    color: #2d3748;
    line-height: 1.5;
    max-height: 600px;
    overflow-y: auto;
    padding: 1rem;
}

.intro-content :deep(div) {
    margin-bottom: 1rem;
}

.intro-content :deep(strong) {
    font-weight: 600;
    color: #222222;
}

.intro-content :deep(ul) {
    list-style-type: disc;
    padding-left: 2rem;
    margin: 1rem 0;
}

.intro-content :deep(li) {
    margin-bottom: 0.5rem;
}

.intro-content :deep(em) {
    font-style: italic;
    color: #666666;
}

.intro-heading {
    font-weight: 600;
    margin-bottom: 1rem;
}

.links-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.links-list li {
    margin-bottom: 0.75rem;
}

.links-list li:last-child {
    margin-bottom: 0;
}

.links-list a {
    color: #00796b;
    text-decoration: none;
    font-size: 0.9rem;
}

.links-list a:hover {
    text-decoration: underline;
}

.attachments-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.attachment-item {
    margin-bottom: 0.75rem;
}

.attachment-item:last-child {
    margin-bottom: 0;
}

.file-name {
    color: #00796b;
    text-decoration: none;
    font-size: 0.9rem;
}

.file-name:hover {
    text-decoration: underline;
}

.no-data {
    color: #a0aec0;
    font-style: italic;
    font-size: 0.9rem;
}

.action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
}

.action-buttons .status-message {
    margin: 0 auto;
}

.status-message {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
}

.status-message.declined {
    color: #D32F2F;
}

.status-message i {
    font-size: 1.25rem;
}

.pricing-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.pricing-table th,
.pricing-table td {
    border: 1px solid #e2e8f0;
    padding: 0.75rem 1rem;
    text-align: left;
}

.pricing-table th {
    background: #f8fafc;
    font-weight: 600;
}

.pricing-tables-btns-intro {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
    margin-bottom: 2rem;
}


.pricing-tables-toggle-container {
    background-color: #ffffff;
    padding: 0.5rem;
    border-radius: 6px;
    display: flex;
    gap: 0.5rem;
    overflow-x: auto;
    max-width: 80%;
    scrollbar-width: thin;
    scrollbar-color: #292B32 #ffffff;
}

/* Webkit scrollbar styles */
.pricing-tables-toggle-container::-webkit-scrollbar {
    height: 6px;
}

.pricing-tables-toggle-container::-webkit-scrollbar-track {
    background: #ffffff;
    border-radius: 3px;
}

.pricing-tables-toggle-container::-webkit-scrollbar-thumb {
    background-color: #292B32;
    border-radius: 3px;
}

.pricing-tables-toggle-container::-webkit-scrollbar-thumb:hover {
    background-color: #1a1b20;
}

.pricing-tables-toggle-container button,
.base-rate-toggle-container button {
    border: none;
    outline: none;
    background-color: #ffffff;
    color: #555555;
    padding: 0.5rem 1rem;
    white-space: nowrap;
    min-width: auto !important;
    width: auto !important;
    flex-shrink: 0;
}

.pricing-tables-toggle-container button:hover {
    background-color: #292B32;
    color: #ffffff;
}

.pricing-tables-toggle-container button.active {
    background-color: #292B32;
    color: #ffffff;
}

.pricing-tables-tabview ::v-deep(.p-tabview-nav-container) {
    display: none !important;
}

.pricing-table-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.base-rate-search-download {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
}

.base-rate-toggle-container {
    background-color: #E5F0EE;
    padding: 0.5rem;
    border-radius: 6px;
    display: flex;
    gap: 0.5rem;
}

.base-rate-toggle-container button.baseRateActive {
    background-color: #00796b;
    color: #ffffff;
}

.message-icon {
    width: 12px;
    height: 12px;
    margin-right: 8px;
}

.base-rate-download-btn {
    background-color: #ffffff;
    color: #555555;
    border: 1px solid #E6E6E6;
    border-radius: 6px;
    width: 32px !important;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #00796b;
}

.pricing-table-section-title {
    font-size: 18px;
    font-weight: 600;
    color: #222222;
}

.skeleton-section {
    margin-bottom: 2rem;
}

.skeleton-list {
    padding-left: 2rem;
}

.mb-2 {
    margin-bottom: 0.5rem;
}

.question-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.question-title {
    font-size: 0.9rem;
    color: #333;
}

/* Mandatory field styling with red border */
.mandatory-field {
    border: 1px solid #ff4d4f !important;
    /* background-color: #fff2f0 !important; */
    border-radius: 4px !important;
}

.mandatory-field:focus {
    border-color: #ff4d4f !important;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
    border-radius: 4px !important;
}

/* PrimeVue component specific mandatory field styling */
:deep(.p-inputtext.mandatory-field),
:deep(.p-dropdown.mandatory-field),
:deep(.p-multiselect.mandatory-field),
:deep(.p-calendar.mandatory-field),
:deep(.p-inputnumber.mandatory-field),
:deep(.p-textarea.mandatory-field) {
    border-color: #ff4d4f !important;
    border-radius: 4px !important;
    /* background-color: #fff2f0 !important; */
}

:deep(.p-inputtext.mandatory-field:focus),
:deep(.p-dropdown.mandatory-field:focus),
:deep(.p-multiselect.mandatory-field:focus),
:deep(.p-calendar.mandatory-field:focus),
:deep(.p-inputnumber.mandatory-field:focus),
:deep(.p-textarea.mandatory-field:focus) {
    border-color: #ff4d4f !important;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
    border-radius: 4px !important;
}

/* File upload mandatory field styling */
.file-upload-wrapper .mandatory-field + .file-upload-label {
    border-color: #ff4d4f;
    /* background-color: #fff2f0; */
}

.options-cell {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.option-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.text-input {
    width: 100%;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 1rem;
}

:deep(.p-checkbox) {
    margin-right: 0.5rem;
}

.company-profile-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.company-profile-question {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.company-profile-label {
    font-weight: 600;
    color: #222;
}

.company-profile-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.company-profile-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.company-profile-input {
    width: 100%;
}

.required-input {
    display: none;
}

/* Remove the old valid-input styling since we're using mandatory-field now */

.file-upload-container {
    width: 150px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.file-upload-wrapper {
    position: relative;
    width: 100%;
}

.file-upload-input {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: 2;
}

.file-upload-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #f8f9fa;
    color: #495057;
    transition: all 0.2s ease;
}

.file-upload-label i {
    font-size: 1rem;
}

.file-upload-label:hover {
    background-color: #e9ecef;
}

.file-upload-label:hover {
    background-color: #e9ecef;
}

.file-name-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background-color: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #ced4da;
}

.file-name {
    flex: 1;
    font-size: 0.9rem;
    color: #495057;
    word-break: break-all;
    width: 90%;
}

.remove-file-btn {
    width: 10% !important;
    padding: 0.25rem;
    min-width: 2rem;
    height: 2rem;
}

/* Style for valid state */
.file-upload-wrapper .valid-input + .file-upload-label {
    border-color: #52c41a;
    background-color: #f6ffed;
}

.question-group {
    margin-bottom: 2rem;
    padding: 1.5rem;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.question-group-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e2e8f0;
}

.company-profile-question {
    margin-bottom: 1.5rem;
}

.company-profile-question:last-child {
    margin-bottom: 0;
}

.upload-progress {
    margin-top: 0.5rem;
    width: 100%;
}

:deep(.p-progressbar) {
    height: 0.5rem;
    background: #f0f0f0;
}

:deep(.p-progressbar .p-progressbar-value) {
    background: #00796b;
}

.retract-confirmation {
    padding: 1rem 0;
}

.retract-confirmation p {
    margin: 0;
    color: #333;
    font-size: 1rem;
    line-height: 1.5;
}

:deep(.p-calendar) {
    width: 200px;
}

:deep(.p-calendar .p-inputtext) {
    width: 100%;
}

:deep(.p-calendar-w-btn .p-datepicker-trigger.p-button) {
    background-color: #00796b !important;
    border: 0 none !important;
}

.pricing-table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}


.nested-tables-nav {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 6px;
    width: 600px;
    background: #E5F0EE;
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: #245B5F #E5F0EE;
}

/* Webkit scrollbar styles */
.nested-tables-nav::-webkit-scrollbar {
    height: 6px;
}

.nested-tables-nav::-webkit-scrollbar-track {
    background: #E5F0EE;
    border-radius: 3px;
}

.nested-tables-nav::-webkit-scrollbar-thumb {
    background-color: #245B5F;
    border-radius: 3px;
}

.nested-tables-nav::-webkit-scrollbar-thumb:hover {
    background-color: #1a3f42;
}

.nested-table-btn {
    background-color: transparent;
    color: #333333;
    white-space: nowrap;
    min-width: auto !important;
    width: auto !important;
    flex-shrink: 0;
}

.nested-table-btn.active {
    background-color: #245B5F;
    color: #ffffff;
}

.nested-table-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 1rem;  
}

.nested-table-name {
    flex: 1;
    font-size: 0.9rem;
    color: #495057;
    word-break: break-all;
    width: 90%;
}

.add-table-btn {
    min-width: 100px !important;
}

.no-nested-table {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    color: #6c757d;
    font-style: italic;
}

.custom-sidebar-header{
    display: flex;
    align-items: center;
    height: 60px !important;
    border-bottom: 1px solid #e0e0e0;
    padding: 0px 10.5px;

}

.sidebar-header-left{
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    width: 30%;
    border-right: 1px solid #e0e0e0;
    height: 100%;
    padding-right: 10.5px;
}

.sidebar-header-left .icon-btn{
    width: 3rem !important;
    background-color: #333;
}

.sidebar-header-left .sidebar-header-title,
.sidebar-header-right .sidebar-header-title{
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.sidebar-header-right {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1;
    padding-left: 10.5px;
}

.sidebar-header-right .close-btn{
    width: 3rem !important;
    background-color: transparent;
    color: #333;
}

/* Try multiple approaches to target the header content */
:deep(.p-sidebar-header-content) {
    width: 100% !important;
}

:deep(.p-sidebar .p-sidebar-header-content) {
    width: 100% !important;
}

:deep(.p-sidebar-header) {
    width: 100% !important;
}

/* Global style as fallback */
:global(.p-sidebar-header-content) {
    width: 100% !important;
}

:global(.p-sidebar-header) {
    padding: 0 !important;
}

:global(.p-sidebar-content) {
    padding: 0 10.5px !important;
}

.sidebar-header-right-container{
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.sidebar-header-right-container-close {
    width: 100%;
    display: flex
;
    justify-content: flex-end;
    align-items: center;
}

.nested-table-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.edit-confirm-btn,
.edit-cancel-btn {
    width: 2rem !important;
    height: 2rem !important;
    padding: 0.25rem !important;
    min-width: auto !important;
}

.edit-confirm-btn {
    background-color: #5cb85c !important;
    color: #fff !important;
}

.edit-cancel-btn {
    background-color: #d9534f !important;
    color: #fff !important;
}

.nested-table-edit-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.nested-table-edit-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #f8f9fa;
    color: #495057;
}

.nested-table-edit-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 150px;
}

.nested-table-edit-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: #ffffff;
    color: #495057;
    font-size: 0.9rem;
    min-width: 120px;
}

.nested-table-edit-input:focus {
    outline: none;
    border-color: #00796b;
    box-shadow: 0 0 0 2px rgba(0, 121, 107, 0.2);
}

.nested-table-item {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.edit-confirm-btn,
.edit-cancel-btn {
    width: 2rem !important;
    height: 2rem !important;
    padding: 0.25rem !important;
    min-width: auto !important;
    border-radius: 4px !important;
}

.edit-confirm-btn {
    background-color: #28a745 !important;
    color: #fff !important;
    border: 1px solid #28a745 !important;
}

.edit-confirm-btn:hover {
    background-color: #218838 !important;
    border-color: #1e7e34 !important;
}

.edit-cancel-btn {
    background-color: #dc3545 !important;
    color: #fff !important;
    border: 1px solid #dc3545 !important;
}

.edit-cancel-btn:hover {
    background-color: #c82333 !important;
    border-color: #bd2130 !important;
}

</style>